"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePositionHistory = exports.usePositionCashflow = void 0;
const utils_1 = require("../../../utils");
const react_1 = require("react");
const trade_utils_lib_1 = require("trade_utils_lib");
const FutureAPI_1 = require("../../../apis/FutureAPI");
const interface_1 = require("../../../interface");
const CalculateUtils_1 = require("../../Trade/FutureTrade/CalculateUtils");
const SymbolComponent_1 = require("../../FuturesConfig/SymbolComponent");
const FuturesConfig_1 = require("../../FuturesConfig");
var usePositionCashflow_1 = require("./usePositionCashflow");
Object.defineProperty(exports, "usePositionCashflow", { enumerable: true, get: function () { return usePositionCashflow_1.usePositionCashflow; } });
function mergeTimeRange(filterRange, lastTime) {
    return lastTime ? Object.assign(Object.assign({}, filterRange), { endTime: lastTime }) : filterRange;
}
function getFeeValue(summary, key, quote) {
    var _a, _b, _c, _d;
    if (((_b = (_a = summary === null || summary === void 0 ? void 0 : summary[key]) === null || _a === void 0 ? void 0 : _a.items) === null || _b === void 0 ? void 0 : _b.length) > 0) {
        return (_d = (_c = summary[key].items.find((item) => item.coin === quote)) === null || _c === void 0 ? void 0 : _c.amount) !== null && _d !== void 0 ? _d : "";
    }
    return "";
}
// 在此时间戳后，按照 summary 展示已实现盈亏，在此时间戳之前的历史仓位，若有summary展示summary，无summary展示旧的已实现盈亏
const showSummaryTime = 1706267980818;
function processHistoryPosition(value) {
    const { qty_long, qty_short = "", amount_long = "", amount_short = "", amount_settle = "", leverage: _leverage, position_side, symbol, amount_liquidated, qty_liquidated, isolated_mode, quote_snapshot, quote_net_input, qty_takeover, amount_takeover, summary, created_time, } = value;
    const symbolInfo = SymbolComponent_1.SymbolPerpetualUSDTComponent.getExchangeSymbol(symbol);
    const currency = (symbolInfo === null || symbolInfo === void 0 ? void 0 : symbolInfo.quote) || "";
    // 是否被强平过（有可能被降档强平果一部分）
    const liquidated = new trade_utils_lib_1.SafeDecimal(amount_liquidated).abs().gt(0);
    const liqFeeRate = liquidated ? CalculateUtils_1.CalculateUtils.getSymbolLiqFeeRate(symbol) : "";
    // 判断是否是接管状态，强平状态下qty_takeover字段大于0
    // 强平时将使用不一样的公式来计算盈亏以及盈亏比率
    // 计算逻辑详见文档 https://pionex.atlassian.net/wiki/spaces/future/pages/241762408/-
    // 判断是否被接管，后端建议这么判断，而不是判断 value.position_flag === FuturePositionFlag.TAKEOVER
    const isTakeover = new trade_utils_lib_1.SafeDecimal(qty_takeover).abs().gt(0);
    const leverage = new trade_utils_lib_1.SafeDecimal(_leverage || 1);
    const isLong = position_side === interface_1.FuturePositionSide.LONG;
    const positionQty = new trade_utils_lib_1.SafeDecimal(isLong ? qty_long : qty_short);
    const positionSide = isLong ? 1 : -1;
    const positionQtyABS = positionQty.abs();
    const avgShort = new trade_utils_lib_1.SafeDecimal(amount_short).div(qty_short).mul(-1);
    const avgLong = new trade_utils_lib_1.SafeDecimal(amount_long).div(qty_long).mul(-1);
    const [costAvgPrice, avgPrice] = isLong ? [avgLong, avgShort] : [avgShort, avgLong];
    const amountSettle = new trade_utils_lib_1.SafeDecimal(amount_settle).mul(-1);
    // 实现盈亏
    let realizedPnl = amountSettle;
    // 实现盈亏率
    let realizedPnlRate = avgPrice.sub(costAvgPrice).div(costAvgPrice).mul(leverage).mul(positionSide);
    // 发生过强平（或是降档强平）
    if (liquidated) {
        // 强平手续费
        const liqFee = liquidated ? new trade_utils_lib_1.SafeDecimal(amount_liquidated).abs().mul(new trade_utils_lib_1.SafeDecimal(liqFeeRate)) : 0;
        // 获取平仓最终盈亏
        realizedPnl = amountSettle.sub(liqFee);
        realizedPnlRate = positionQty.mul(avgPrice.sub(costAvgPrice)).sub(liqFee).div(positionQtyABS.mul(costAvgPrice).div(leverage));
    }
    // 接管均价
    let takeoverPrice = undefined;
    // 被接管
    if (isTakeover) {
        // 接管金额
        const amountTakeover = new trade_utils_lib_1.SafeDecimal(amount_takeover);
        if (amountTakeover.eq(0)) {
            // 老仓位，服务端没有值
            // amount_liquidated / qty_liquidated
            const qtyLiqui = new trade_utils_lib_1.SafeDecimal(qty_liquidated);
            if (qtyLiqui.eq(0) === false) {
                takeoverPrice = new trade_utils_lib_1.SafeDecimal(amount_liquidated).div(qtyLiqui).abs().toString();
            }
        }
        else {
            // amount_takeover / qty_takeover
            // 接管数量
            const qtyTakeover = new trade_utils_lib_1.SafeDecimal(qty_takeover);
            // 接管均价
            takeoverPrice = amountTakeover.div(qtyTakeover).abs().toString();
        }
        // 逐仓模式下
        if (isolated_mode !== interface_1.FutureOrderMarginType.CROSS) {
            // 净转入
            const quoteNetInput = new trade_utils_lib_1.SafeDecimal(quote_net_input);
            // 有净转入时持仓盈亏用净转入
            if (!quoteNetInput.eq(0)) {
                realizedPnl = quoteNetInput.mul(-1);
            }
            else {
                // 没有净转入时
                realizedPnl = realizedPnl.sub(trade_utils_lib_1.SafeDecimal.min(new trade_utils_lib_1.SafeDecimal(quote_snapshot), 0));
            }
        }
    }
    let nSummary;
    if (summary) {
        const fundingFee = getFeeValue(summary, "FUNDING_FEE", currency);
        const tradingFee = getFeeValue(summary, "TRADING_FEE", currency);
        let liquidationFee = getFeeValue(summary, "LIQUIDATION_FEE", currency);
        let positionSettlement = getFeeValue(summary, "POSITION_SETTLEMENT", currency);
        // 逐仓时，强平手续费需要加上回补
        if (isolated_mode !== interface_1.FutureOrderMarginType.CROSS) {
            const remedy = getFeeValue(summary, "REMEDY", currency);
            liquidationFee = new trade_utils_lib_1.SafeDecimal(liquidationFee).add(new trade_utils_lib_1.SafeDecimal(remedy)).toString();
        }
        // 仓位净盈亏 = 仓位盈亏+资金费+手续费
        realizedPnl = new trade_utils_lib_1.SafeDecimal(positionSettlement).add(new trade_utils_lib_1.SafeDecimal(fundingFee)).add(new trade_utils_lib_1.SafeDecimal(tradingFee));
        // 爆仓时，还需要加上强平手续费
        if (isTakeover) {
            realizedPnl = realizedPnl.add(new trade_utils_lib_1.SafeDecimal(liquidationFee));
        }
        // 重新计算净盈亏率，净盈亏率 = 净利润 / (持仓均价 * 持有 base 量 / 杠杆倍数)
        realizedPnlRate = new trade_utils_lib_1.SafeDecimal(realizedPnl).div(new trade_utils_lib_1.SafeDecimal(costAvgPrice).mul(positionQtyABS).div(leverage));
        nSummary = {
            fundingFee,
            liquidationFee,
            positionSettlement,
            tradingFee,
        };
        nSummary = Object.keys(nSummary).reduce((obj, key) => {
            if (!new trade_utils_lib_1.SafeDecimal(nSummary[key]).isZero()) {
                obj[key] = nSummary[key];
            }
            return obj;
        }, {});
    }
    return Object.assign(Object.assign({}, value), { realizedPnl: realizedPnl.toString(), realizedPnlRate: realizedPnlRate.toString(), positionQuantity: positionQtyABS.toString(), costAvgPrice: costAvgPrice.toString(), avgPrice: avgPrice.toString(), takeoverPrice: takeoverPrice, currency, currencyPrecision: (0, FuturesConfig_1.getFuturesCoinPrecision)(currency), summary: nSummary, originSummary: summary, isTakeover, hidePnl: !CalculateUtils_1.CalculateUtils.isVirtualSymbol(currency || "") && new trade_utils_lib_1.SafeDecimal(created_time).gt(showSummaryTime) && !nSummary });
}
const PositionHistoryModelsPageLength = 12;
/**
 * 获取合约历史仓位列表，支持refresh跟loadMore
 */
const usePositionHistory = (props) => {
    const { symbol, positionFlag, autoRefresh = true, timeRange, limit = PositionHistoryModelsPageLength } = props;
    const [hasMore, setHasMore] = (0, react_1.useState)(true);
    const [positions, setPositions] = (0, react_1.useState)();
    const [error, setError] = (0, react_1.useState)();
    const [refreshing, setRefreshing] = (0, react_1.useState)(true);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const autoRefreshRef = (0, react_1.useRef)(autoRefresh);
    autoRefreshRef.current = autoRefresh;
    const endTime = (0, react_1.useMemo)(() => {
        if (positions === null || positions === void 0 ? void 0 : positions.length) {
            return positions[positions.length - 1].updated_time;
        }
        return undefined;
    }, [positions]);
    const loadingRef = (0, react_1.useRef)(false);
    /**校准之后的时间区间，结束时间需要加一天 */
    const timeRangeAdjusted = (0, react_1.useMemo)(() => (0, utils_1.adjustTimeRange)(timeRange), [timeRange]);
    const queryPositions = (0, react_1.useCallback)((lastTime) => {
        const request = { limit };
        symbol && (request.symbol = symbol);
        // 设置按照时间筛选的参数
        const range = mergeTimeRange(timeRangeAdjusted, lastTime);
        if (range) {
            range.endTime && (request.end_time = Number(range.endTime));
            range.startTime && (request.start_time = Number(range.startTime));
        }
        if (positionFlag) {
            request.position_flag = positionFlag;
        }
        return (0, FutureAPI_1.queryHistoryPositions)(request);
    }, [limit, positionFlag, symbol, timeRangeAdjusted]);
    const refresh = (0, react_1.useCallback)(() => {
        setRefreshing(true);
        loadingRef.current = true;
        return queryPositions().subscribe({
            next: (data) => {
                setRefreshing(false);
                setError(null);
                // fix 避免后端返回null时报错
                const positionList = data.positions || [];
                setHasMore(positionList.length >= limit);
                setPositions(positionList.map(processHistoryPosition));
                loadingRef.current = false;
            },
            error: (error) => {
                setRefreshing(false);
                setError(error);
                setHasMore(false);
                loadingRef.current = false;
            },
        });
    }, [limit, queryPositions]);
    const loadMore = (0, react_1.useCallback)(() => {
        if (!queryPositions || !endTime || !hasMore || !(positions === null || positions === void 0 ? void 0 : positions.length) || loadingRef.current) {
            return;
        }
        loadingRef.current = true;
        setLoading(true);
        return queryPositions(endTime).subscribe({
            next: (data) => {
                setError(null);
                if (data) {
                    const newPositions = data.positions || [];
                    setHasMore(newPositions.length >= limit);
                    setPositions((v) => {
                        return v === null || v === void 0 ? void 0 : v.concat(newPositions.map(processHistoryPosition));
                    });
                }
                loadingRef.current = false;
                setLoading(false);
            },
            error: (_error) => {
                setHasMore(false);
                loadingRef.current = false;
                setLoading(false);
            },
        });
    }, [queryPositions, endTime, hasMore, positions === null || positions === void 0 ? void 0 : positions.length, limit]);
    // 监听关键信息的变跟，并刷新页面
    (0, react_1.useEffect)(() => {
        const sub = refresh();
        return () => {
            sub === null || sub === void 0 ? void 0 : sub.unsubscribe();
            loadingRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [positionFlag, symbol, timeRangeAdjusted]);
    return { positions, refreshing, loading, error, hasMore, refresh, loadMore };
};
exports.usePositionHistory = usePositionHistory;
